import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { FC } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { Alignment } from '@app/shared/enums/alignment.enum';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';
import TableSectionValuesPerMultiple from '../../components/TableSectionValuesPerMultiple';
import TableSectionValuesPerCase from '../../components/TableSectionValuesPerCase';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  getWaterfallCaseValue,
  getWaterfallCaseYearMultipleValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';

interface ExitDistributionsSectionProps {
  columnsCount: number;
  hasInterimDistributions?: boolean;
}

export const ExitDistributionsSection: FC<ExitDistributionsSectionProps> = ({
  columnsCount,
  hasInterimDistributions,
}): JSX.Element => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const { l } = useLocale();

  return (
    <>
      {hasInterimDistributions && (
        <tr data-testid="sponsor-exit-distributions-header-row">
          <th className="table-primary__cell--header-tertiary">{l('_ExitDistributions')}</th>
          <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
        </tr>
      )}
      <TableSectionValuesPerMultiple
        title={l('_ShareholderDebtAtExit')}
        renderCell={({ caseItem, forecast, multipleId }) => (
          <CellValue
            {...amountRules}
            value={getWaterfallCaseYearMultipleValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              multipleId!,
              (multiple) => multiple.institutionalShareholderDebtDistributions
            )}
            dataTestId={`shareholder-debt-exit-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
          />
        )}
      />
      <TableSectionValuesPerMultiple
        title={l('_OrdinaryEquityAtExit')}
        renderCell={({ caseItem, forecast, multipleId }) => (
          <CellValue
            {...amountRules}
            value={getWaterfallCaseYearMultipleValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              multipleId!,
              (multiple) => multiple.institutionalOrdinaryEquityDistributions
            )}
            dataTestId={`ordinary-equity-exit-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
          />
        )}
      />

      <TableSectionValuesPerMultiple
        title={l('_TotalReturn')}
        renderCell={({ caseItem, forecast, multipleId }) => (
          <CellValue
            {...amountRules}
            value={getWaterfallCaseYearMultipleValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              multipleId!,
              (multiple) => multiple.institutionalReturn
            )}
            dataTestId={`total-return-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
          />
        )}
      />
      <TableSectionValuesPerCase
        title={l('_WeightedSponsorReturn')}
        renderSpacer={false}
        renderCell={({ caseItem }) => (
          <CellValue
            {...amountRules}
            alignment={Alignment.Center}
            value={getWaterfallCaseValue(
              results,
              caseItem.caseId,
              (caseItem) => caseItem.weightedInstitutionalReturn
            )}
            dataTestId={`weighted-sponsor-return-cell-${caseItem.caseId}`}
          />
        )}
        additionalTitleClassNames="table-primary__cell--strong"
        additionalCellClassNames="table-primary__cell--strong"
      />
      <tr data-testid="case-weighted-sponsor-return-row">
        <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--strong">
          {l('_CaseWeightedSponsorReturn')}
        </th>
        <td className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator" />
        <td
          colSpan={columnsCount - 2}
          className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--strong">
          <CellValue
            {...amountRules}
            alignment={Alignment.Center}
            value={results.waterfall.weightedInstitutionalReturn}
            dataTestId="case-weighted-sponsor-return-cell"
          />
        </td>
      </tr>
      <TableSpacerRow />
    </>
  );
};
